import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ChipsInput } from "./ChipsInput";
import type { MonitorIR, Polarity } from "@/monitors/schemas";

interface Props {
  value: MonitorIR;
  onChange: (next: MonitorIR) => void;
  categories: string[];
  materials: string[];
  readOnly?: boolean;
}

export function IRForm({ value, onChange, categories, materials, readOnly }: Props) {
  const set = <K extends keyof MonitorIR>(key: K, v: MonitorIR[K]) =>
    onChange({ ...value, [key]: v });

  return (
    <div className="space-y-4">
      <Section title="Required entities">
        <ChipsInput
          value={value.required_entities}
          onChange={(v) => set("required_entities", v)}
          placeholder="Add an actor (Trump, ECB, China…)"
          ariaLabel="Required entities"
        />
      </Section>

      <div className="grid grid-cols-2 gap-4">
        <Section title="Action class">
          <Input
            value={value.action_class}
            onChange={(e) => set("action_class", e.target.value)}
            disabled={readOnly}
          />
        </Section>
        <Section title="Polarity">
          <Select
            value={value.polarity}
            onValueChange={(v) => set("polarity", v as Polarity)}
            disabled={readOnly}
          >
            <SelectTrigger aria-label="Polarity">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="positive">positive</SelectItem>
              <SelectItem value="negative">negative</SelectItem>
              <SelectItem value="either">either</SelectItem>
            </SelectContent>
          </Select>
        </Section>
      </div>

      <Section title="Must include (soft hint)">
        <ChipsInput
          value={value.must_include_terms}
          onChange={(v) => set("must_include_terms", v)}
          variant="include"
          ariaLabel="Must include terms"
        />
      </Section>

      <Section title="Must exclude (hard rule — protects polarity)">
        <ChipsInput
          value={value.must_exclude_terms}
          onChange={(v) => set("must_exclude_terms", v)}
          variant="exclude"
          ariaLabel="Must exclude terms"
        />
      </Section>

      <div className="grid grid-cols-2 gap-4">
        <Section title="Category hints">
          <ChipPicker
            options={categories}
            selected={value.category_hints}
            onChange={(v) => set("category_hints", v)}
            ariaLabel="Category hints"
          />
        </Section>
        <Section title="Material hints">
          <ChipPicker
            options={materials}
            selected={value.material_hints}
            onChange={(v) => set("material_hints", v)}
            ariaLabel="Material hints"
          />
        </Section>
      </div>

      {value.rationale && (
        <Section title="Why the LLM thinks this matters">
          <p className="text-sm text-muted-foreground italic">{value.rationale}</p>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">
        {title}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ChipPicker({
  options,
  selected,
  onChange,
  ariaLabel,
}: {
  options: string[];
  selected: string[];
  onChange: (v: string[]) => void;
  ariaLabel: string;
}) {
  return (
    <div className="flex flex-wrap gap-1" aria-label={ariaLabel}>
      {options.map((opt) => {
        const active = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() =>
              onChange(active ? selected.filter((x) => x !== opt) : [...selected, opt])
            }
            className="focus:outline-none"
          >
            <Badge variant={active ? "default" : "outline"}>{opt}</Badge>
          </button>
        );
      })}
    </div>
  );
}
